import { getLogger } from "@expert/logging";
import { Flex } from "@mantine/core";
import { useMemo } from "react";
import { withErrorBoundary } from "react-error-boundary";
import { ChatManager } from "@/components/chat";
import { env } from "@/config";
import type { ExpertUniversitySimulatedVoiceTask } from "@/expert-university";
import { useExpertUniversity } from "@/expert-university/hooks/useExpertUniversity";
import { NotificationContainer } from "@/notification/NotificationContainer";
import { Panels } from "@/panels";
import { MicroLearning } from "@/sales-tools";
import { Announcement } from "@/sales-tools/challenges/Announcement";
import type { AgentActivity, AgentSdk, ChatTask, CustomerSession, TrainingTask, WithoutCustomerSession } from "@/sdk";
import { isVoiceTask, useActiveTask } from "@/sdk";
import { createFallbackRender } from "@/shared-utils";
import { Troubleshooting } from "@/troubleshoot";
import { WorkspaceContentLeftSidebarSlot } from "./WorkspaceContentLeftSidebarSlot";
import { WorkspaceContentLeftTopASlot } from "./WorkspaceContentLeftTopASlot";
import { WorkspaceContentRightSidebarSlot } from "./WorkspaceContentRightSidebarSlot";
import { WorkspaceContentTopbarSlot } from "./WorkspaceContentTopbarSlot";

const logger = getLogger({ module: "Workspace" });

const MicroLearningModalHandled = withErrorBoundary(MicroLearning, { fallbackRender: createFallbackRender(logger) });
const AnnouncementModalHandled = withErrorBoundary(Announcement, { fallbackRender: createFallbackRender(logger) });

export function WorkspaceContent(props: {
    activeExpertUniversityTask: ExpertUniversitySimulatedVoiceTask | null;
    activeTrainingTask: TrainingTask | null;
    callControlsEnabled: boolean;
    challengeEnabled: boolean;
    chatTasks: ChatTask[];
    currentActivity: AgentActivity | undefined;
    customerPanelEnabled: boolean;
    customerSession: boolean;
    hasCallEnded: boolean;
    microLearningV2Enabled: undefined | boolean;
    showRealtimeTranscript: boolean;
    sdk: AgentSdk;
    session: CustomerSession | WithoutCustomerSession;
    showExpertUniversity: undefined | boolean;
    troubleshooting: boolean;
}) {
    const activeTask = useActiveTask();
    useExpertUniversity();
    const activeVoiceTask = useMemo(() => {
        return isVoiceTask(activeTask) ? activeTask : null;
    }, [activeTask]);

    return (
        <Flex flex={1}>
            {env.channel === "chat" ? (
                <ChatManager chatTasks={props.chatTasks} />
            ) : (
                <Panels
                    // Aside Panels
                    leftSidebarSlot={
                        <WorkspaceContentLeftSidebarSlot
                            showRealtimeTranscript={props.showRealtimeTranscript}
                            taskStartTimeInMs={props.session.startedTimestamp}
                        />
                    }
                    leftSidebarConfig={{
                        isCollapsed: Boolean(props.showExpertUniversity),
                    }}
                    topbarSlot={<WorkspaceContentTopbarSlot activeVoiceTask={activeVoiceTask} />}
                    topbarConfig={{
                        isCollapsed: Boolean(props.showExpertUniversity) || !props.callControlsEnabled,
                    }}
                    rightSidebarSlot={
                        <WorkspaceContentRightSidebarSlot
                            customerSession={props.customerSession}
                            customerPanelEnabled={props.customerPanelEnabled}
                            activeExpertUniversityTask={props.activeExpertUniversityTask}
                        />
                    }
                    rightSidebarConfig={{
                        isCollapsed: Boolean(props.showExpertUniversity),
                    }}
                    // Main Panels
                    leftTopASlot={
                        <WorkspaceContentLeftTopASlot
                            showExpertUniversity={props.showExpertUniversity}
                            activeTrainingTask={props.activeTrainingTask}
                        />
                    }
                    leftTopConfig={{
                        a: { title: "EXPERT ASSIST", isMinimized: false, isClosed: false, showHeader: false },
                    }}
                />
            )}

            {/* Modals go outside Panels */}
            {(props.challengeEnabled || props.microLearningV2Enabled) &&
            props.hasCallEnded &&
            !props.customerSession &&
            props.currentActivity === props.sdk.availableAgentActivity ? (
                <>
                    <AnnouncementModalHandled />
                    <MicroLearningModalHandled />
                </>
            ) : null}

            {/* Modal */}
            {(props.challengeEnabled || props.microLearningV2Enabled) &&
                props.currentActivity === props.sdk.defaultAgentActivity && <AnnouncementModalHandled />}

            {/* Modal */}
            {props.troubleshooting ? <Troubleshooting /> : null}

            {/* Notifications are global and outside of Panels */}
            <NotificationContainer />
        </Flex>
    );
}
